import React, { JSX } from "react";
import {
  Container,
  IconLedWithGradientBlue, IconLedWithGradientRed,
  IconLedWithGradientOrange,
  IconLightCommon
} from "./Header.styled";
import { BrandLink } from "../BrandLink/BrandLink";
import {
  IconArrowLeft,
  IconArrowSharpLeft,
  IconLight,
  IconLogo,
  IconMoon,
  IconRectangle,
  IconSun
} from "../Icons/Icons";
import { FloatIndicator } from "../FloatIndicator/FloatIndicator";
import { Box, SxProps, Theme, Typography, useMediaQuery, useTheme } from "@mui/material";
import { colors, transitions } from "../../styles/theme";
// import ImageLogo from ".././../assets/images/logo.png";
import classNames from "classnames";
import { convertToBreakpoints, getFluidsByKeysFunc } from "../../utils/fuilds";
import { pxToVw } from "../../utils/common";
import { MODELS_DATA, ProductNameEnum, UID, UIMode } from "@shared/core";
import { useConfigStore } from "../../store";
import { BREAKPOINT_MOBILE, LED, LIGHT_MODES, LOCALES } from "../../constants";
import { deviceStyles, useDeviceStyles } from "../../hooks/useDeviceStyles";
import { useResponsiveScaler } from "../../lib/ResponsiveScaler";
import { DropdownBrandLink } from "../Dropdown/Dropdown";
import { useActiveProduct } from "../../store/selectors";

export type HeaderProps = {
  onChangeModel?: (uid: ProductNameEnum) => void;
  onChangeMode?: (v: string) => void;
  onChangeLed?: (v: string) => void;
  onChangeLocale?: (v: string) => void;
} & React.ComponentProps<typeof Container>;

const LIGHT_MODES_DATA: Record<string, {
  iconComponent: any,
  sx: SxProps<Theme>
  mobile: SxProps<Theme>
}> = {
  [LIGHT_MODES.SUNSET]: { iconComponent: IconLight,
    sx: { width: "72px", height: "32px" },
    mobile: { width: "24px", height: "10px" },
  },
  [LIGHT_MODES.DAY]: { iconComponent: IconSun,
    sx: { width: "55px", height: "50px" },
    mobile: { width: "18px", height: "16px" },
  },
  [LIGHT_MODES.NIGHT]: { iconComponent: IconMoon,
    sx: { width: "44px", height: "44px" },
    mobile: { width: "14px", height: "15px" },
  },
};

const LED_PRESETS_DATA: Record<string, {
  iconComponent: any,
  sx: SxProps<Theme>
  mobile: SxProps<Theme>
}> = {
  [LED.ORANGE]: { iconComponent: IconLedWithGradientOrange,
    sx: { width: '20px', height: '20px' },
    mobile: { width: '6px', height: '6px' },
  },
  [LED.BLUE]: { iconComponent: IconLedWithGradientBlue,
    sx: { width: '20px', height: '20px' },
    mobile: { width: '6px', height: '6px' },
  },
  [LED.RED]: { iconComponent: IconLedWithGradientRed,
    sx: { width: '20px', height: '20px' },
    mobile: { width: '6px', height: '6px' },
  },
};

const LIGHT_MODE: UIMode[] = Object.keys(LIGHT_MODES_DATA) as UIMode[];
const LED_PRESET = Object.keys(LED_PRESETS_DATA);

export const Header: React.FC<HeaderProps> = (props): JSX.Element => {
  const {
    onChangeModel,
    onChangeLed,
    onChangeMode,
    onChangeLocale,
    ...rest
  } = props;
  const activeMode = useConfigStore(s => s.products[s.activeProduct]?.ui.mode);
  const activeLed = useConfigStore(s => s.products[s.activeProduct]?.ui.led);
  const activeLocale = useConfigStore(s => s.products[s.activeProduct]?.ui.locale);
  const setMode = useConfigStore(s => s.setMode);
  const setLed = useConfigStore(s => s.setLed);
  const setLocale = useConfigStore(s => s.setLocale);
  const activeIndexMode = LIGHT_MODE.findIndex(el => el === activeMode);
  const activeIndexLed = LED_PRESET.findIndex(el => el === activeLed);
  const activeIndexLocale = LOCALES.findIndex(el => el === activeLocale);

  const { isMobileDevice } = useDeviceStyles();
  const activeProduct = useActiveProduct();

  const { rpx } = useResponsiveScaler();

  return (
    <Container {...rest} sx={{
      ...rpx({}, {
        mobile: {
          padding: "8px 32px 0 32px"
        }
      })
    }}>
      <DropdownBrandLink
        trigger={({ open, toggle }) => (
          <BrandLink
            onClick={toggle}
            icon={isMobileDevice ? IconArrowSharpLeft : IconArrowLeft}
            sx={{
              justifyContent: 'flex-start',
              ...rpx(
                { paddingTop: "30px",
                  svg: {
                    transform: open ? "rotate(-90deg)" : undefined,
                  },},
                {
                  mobile: {
                    gap: "5px",
                    svg: {
                      width: "6px", height: "6px",
                      transform: open ? "rotate(-90deg)" : undefined,
                    },
                  },
                }
              ),
            }}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            Change model
          </BrandLink>
        )}
        menu={({ close }) => (
          <Box sx={{
            display: "flex",
            flexDirection: "column",
          }}>
            {
              Object.entries(MODELS_DATA).map(([key, value]) => (
                <BrandLink
                  key={`change-model-${key}`}
                  sx={(theme) => ({
                    justifyContent: "flex-start",
                    marginBottom: isMobileDevice ? '10px' : '20px',
                  })}
                  typographyProps={{
                    sx: {
                      color: value.uid === activeProduct ? colors.text.v4 : undefined,
                      cursor: value.uid === activeProduct ? "default" : "pointer",
                      textDecoration: "underline",
                      textTransform: "none",
                    }
                  }}
                  onClick={() => {
                    if (value.uid === activeProduct) return;
                    onChangeModel?.(value.uid);
                    close()
                    // setShowChangeModelDialog(false);
                    // setActiveProduct(value.uid);

                  }}>{value.title}</BrandLink>
              ))
            }
          </Box>
        )}
        />

      <Box sx={{
        justifySelf: "center",
        marginTop: isMobileDevice ? "-5px" : "-15px",
      }}
      >
        <IconLogo sx={{
          color: colors.text.v4,
          margin: "0 10px",
          ...rpx({ width: "200px", height: "74px" }, { mobile: { width: "43px", height: "16px" } }),
        }} />
      </Box>


      <Box sx={(theme) => ({
        display: "flex",
        justifySelf: "end",
        alignItems: "center",
        height: "100%",
        // marginRight: '-10px',

        ...rpx(
          { gap: "100px", paddingTop: '30px' },
          { mobile: { gap: '25px' } }
        ),
        // ...convertToBreakpoints(theme, {
        //   xs: { gap: '30px' },
        //   lg: { gap: '45px' },
        //   xxxl: { gap: '100px' },
        // }),

        "& > *": {
          height: "100%"
        }
      })}>
        <FloatIndicator
          isValid={activeIndexMode > -1}
          activeIndex={activeIndexMode >= 0 ? activeIndexMode : 0}
          indicatorSxProps={{ top: "100%" }}
          floatingWidth={isMobileDevice ? 8 : undefined}
          onIndexChange={(newIndex) => {
            const mode = LIGHT_MODE[newIndex];
            const led = mode === "day"
              ? "off"
              : activeLed === "off" ? LED_PRESET[0] : undefined;

            setMode(mode);
            if (led) {
              setLed(led);
              onChangeLed?.(led);
            }
          }}
          sx={rpx({
            gap: "56px"
          })}
          // sx={getFluidsByKeysFunc({
          //   gap: '56px',
          // }, undefined, undefined, {
          //   scale: {
          //     xs: { gap: 1.2 },
          //     sm: { gap: 1.2 },
          //   }
          // })}
        >
          {
            Object.entries(LIGHT_MODES_DATA).map(([key, value]) => (
              <IconLightCommon key={key} sx={{ color: activeMode === key ? colors.text.v4 : colors.icons.v1 }}>
                <Box component={value.iconComponent}
                  /* @ts-ignore */
                   // sx={getFluidsByKeysFunc(value.sx, undefined, undefined, {
                   //   scale: {
                   //     xs: { width: 2, height: 2 },
                   //     sm: { width: 1.8, height: 1.8 }
                   //     // md: { width: 1.5, height: 1.5, },
                   //   }
                   // })}
                  sx={rpx(value.sx, { mobile: value.mobile })}
                />
              </IconLightCommon>
            ))
          }
        </FloatIndicator>


        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "stretch"
          })}
        >
          <Typography variant="v2" sx={(theme) => ({
            letterSpacing: "5px",
            userSelect: "none",
            alignSelf: "center",
            marginRight: "16px",
            transition: transitions.join(["color"]),
            color: activeLed !== "off" ? colors.text.v4 : undefined,
            // marginBottom: '5px',
            // ...rpx({
            //   fontSize: "32px",
            // }, { mobile: { display: "none", } })
            [theme.breakpoints.down(BREAKPOINT_MOBILE)]: {
              // fontSize: "30px",
              display: "none",
            }
          })}>LED</Typography>

          <FloatIndicator
            isValid={activeIndexLed > -1}
            activeIndex={activeIndexLed > -1 ? activeIndexLed : 0}
            indicatorSxProps={{ top: "100%" }}
            floatingWidth={isMobileDevice ? 8 : undefined}
            onIndexChange={(newIndex) => {
              const mode = activeMode;
              const led = LED_PRESET[newIndex];
              setLed(led);
              onChangeLed?.(led);
              if (mode === "day") {
                setMode("night");
              }
            }}
            sx={deviceStyles({
              mobile: {
                gap: "10px"
              }
            }, [{ gap: "10px" }, { gap: "15px" }])}
          >
            {
              Object.entries(LED_PRESETS_DATA).map(([key, value]) => (
                <Box
                  key={key}
                  component={value.iconComponent}
                  className={classNames({ active: activeLed === key })}
                  sx={{
                    color: activeLed === key ? colors.text.v4 : colors.text.v3,
                    // ...rpx(value.sx as any, { mobile: value.mobile } as any)
                  }}
                >
                  <IconRectangle />
                </Box>
              ))
            }
          </FloatIndicator>
        </Box>

        <FloatIndicator
          isValid={activeIndexLocale > -1}
          activeIndex={activeIndexLocale > -1 ? activeIndexLocale : 0}
          indicatorSxProps={{ top: "100%" }}
          floatingWidth={isMobileDevice ? 8 : undefined}
          onIndexChange={(newIndex) => {
            setLocale(LOCALES[newIndex]);
          }}
          sx={{
            ...deviceStyles({
              mobile: { gap: "5px" }
            }, [
              undefined, undefined,
              { gap: "15px" },
              { gap: "20px" }
            ])
          }}
        >
          {
            LOCALES.map(locale => (
              <BrandLink
                key={locale}
                // sx={{ width: "60px" }}
                typographyProps={{
                  sx: {
                    textAlign: "center",
                    fontSize: pxToVw(32),
                    color: activeLocale === locale ? colors.text.v4 : undefined,
                  },
                }}
              >{locale}</BrandLink>
            ))
          }
        </FloatIndicator>

      </Box>
    </Container>
  );
};
